import moment from 'moment'
import { isFunction } from 'lodash'

import { downloadFile, getIdOrNull, requestWithPromise } from '..'
import { HttpMethods } from '../apiHelper'
import apiClient from './apiClient'
import { API_DATE } from '../momentjs'

const XLSX_FORMAT = 'xlsx'
const CSV_FORMAT = 'csv'
const SUPPORTED_EXPORT_FORMATS = [XLSX_FORMAT, CSV_FORMAT]
const JOB_PENDING_STATUS = 404
const JOB_POLLING_TIMEOUT = 10 * 60 * 1000
const DEFAULT_REFRESH_RATE = 1500

export const getJob = (jobId, user) => {
  return requestWithPromise(
    `/job-result/${jobId}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const waitForJobResult = (jobId, user, refreshRate = DEFAULT_REFRESH_RATE) => new Promise((resolve, reject) => {
  const startedAt = Date.now()

  const poll = () => {
    apiClient.get(user, `job-result/${jobId}`).then(json => {
      resolve(json.data.result)
    }).catch(response => {
      if (response?.status === JOB_PENDING_STATUS && Date.now() - startedAt < JOB_POLLING_TIMEOUT) {
        setTimeout(poll, refreshRate)

        return
      }

      reject(response)
    })
  }

  setTimeout(poll, refreshRate)
})

export const makeExport = (exportRoute, user, parameters, onError, onSuccess, refreshRate) => {
  return exportRoute(user, parameters).then(json => {
    if (json?.job) {
      return getDocument(json.job, user, onError, onSuccess, refreshRate)
    }

    if (isFunction(onError)) {
      onError(json)
    }
  })
}

export const getDocument = (jobId, user, onError, onSuccess, refreshRate) => {
  return waitForJobResult(jobId, user, refreshRate ?? DEFAULT_REFRESH_RATE)
    .then(fileName => downloadSpreadsheet(fileName, user))
    .then(() => {
      if (isFunction(onSuccess)) {
        onSuccess()
      }
    })
    .catch(response => {
      if (isFunction(onError)) {
        onError(response)
      }
    })
}

export const downloadSpreadsheet = (fileName, user) => downloadFile(
  '/spreadsheet/download/' + fileName,
  fileName,
  user,
  HttpMethods.GET
)

export const printValidationDocument = (internship, eventCodesTypesLabels, user, timezone = null) => {
  const body = { internship: internship.id, eventCodesTypesLabels }

  if (timezone) {
    body.timezone = timezone
  }

  return apiClient.post(user, 'internship/export-validated-internship', body)
    .then(json => json?.job ? waitForJobResult(json.job, user) : Promise.reject(json))
    .then(fileName => downloadFile('/validation-docs/download/' + fileName, fileName, user, HttpMethods.GET))
}

export class ExportParameters {
  constructor ({ format }) {
    if (!format || !SUPPORTED_EXPORT_FORMATS.includes(format)) {
      format = XLSX_FORMAT
    }

    this.format = format
  }

  toRequestBody () {
    return {
      format: this.format
    }
  }
}

export const exportInstitutionQuotas = (user, institution, { format, startDate, endDate, language, sectorIds = [] }) => {
  return apiClient.post(user, `export/institutions/${getIdOrNull(institution)}/quotas`, { format, startDate, endDate, language, sectorIds })
}

export class PeriodExportParameters extends ExportParameters {
  constructor ({ format, startDate, endDate }) {
    super({ format })

    this.startDate = moment(startDate ?? null)
    this.endDate = moment(endDate ?? null)
  }

  toRequestBody () {
    return {
      end: this.endDate.format(API_DATE),
      format: this.format,
      start: this.startDate.format(API_DATE)
    }
  }
}
